import React, { Fragment, useState, useRef, useCallback, useEffect } from "react";
import Breadcrumb from "../common/breadcrumb";
import Editor from "@monaco-editor/react";
import {
  Card,
  CardBody,
  CardHeader,
  Container,
  Row,
  Col,
  Button,
  Badge,
  Table,
  Spinner,
  Alert,
  Pagination,
  PaginationItem,
  PaginationLink,
} from "reactstrap";
import { Database, Play, Clock, AlertTriangle, List, Download } from "react-feather";
import { executeSqlQuery, getSqlQueryHistory } from "../../utils/apiCalls";
import { successToast, errorToast } from "../../shared/Toast";

const SqlRunner = () => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const editorRef = useRef(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPagination, setHistoryPagination] = useState({ page: 1, totalPages: 1, total: 0 });

  const fetchHistory = useCallback(async (page = 1) => {
    setHistoryLoading(true);
    try {
      const response = await getSqlQueryHistory(page, 20);
      if (response && !response.error) {
        setHistory(response.data?.logs || []);
        setHistoryPagination(response.data?.pagination || { page: 1, totalPages: 1, total: 0 });
      }
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleEditorDidMount = (editor) => {
    editorRef.current = editor;
  };

  const handleExecute = useCallback(async () => {
    if (!query || !query.trim()) {
      errorToast("Please enter a SQL query");
      return;
    }

    setIsLoading(true);
    setError(null);
    setResults(null);

    try {
      const response = await executeSqlQuery(query.trim());

      if (response && !response.isError) {
        setResults(response.data);
        successToast(response.message || "Query executed successfully");
      } else {
        setError(response?.message || "Query execution failed");
      }
      fetchHistory(1);
    } catch (err) {
      setError(err?.message || "An unexpected error occurred");
    } finally {
      setIsLoading(false);
    }
  }, [query]);

  const handleKeyDown = useCallback(
    (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === "Enter") {
        e.preventDefault();
        handleExecute();
      }
    },
    [handleExecute]
  );

  const handleDownloadCsv = useCallback(() => {
    if (!results || !results.rows || results.rows.length === 0) return;

    const escape = (val) => {
      if (val === null || val === undefined) return "";
      const str = typeof val === "object" ? JSON.stringify(val) : String(val);
      return /[",\n\r]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
    };

    const header = results.columns.map(escape).join(",");
    const body = results.rows
      .map((row) => results.columns.map((col) => escape(row[col])).join(","))
      .join("\r\n");
    const csv = `﻿${header}\r\n${body}`;

    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `sql-results-${new Date().toISOString().replace(/[:.]/g, "-")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [results]);

  const renderResultsTable = () => {
    if (!results) return null;

    if (results.queryType === "UPDATE") {
      return (
        <Alert color="success" className="mt-3">
          <strong>UPDATE executed successfully.</strong>
          <br />
          Rows affected: {results.rowsAffected}
          <br />
          Execution time: {results.executionTimeMs}ms
        </Alert>
      );
    }

    if (!results.rows || results.rows.length === 0) {
      return (
        <Alert color="info" className="mt-3">
          Query returned 0 rows. Execution time: {results.executionTimeMs}ms
        </Alert>
      );
    }

    return (
      <div className="mt-3">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <Badge color="primary" className="me-2">
              {results.rowCount} rows
            </Badge>
            <Badge color="secondary">
              <Clock size={12} className="me-1" />
              {results.executionTimeMs}ms
            </Badge>
            {results.rowCount >= results.limitApplied && (
              <Badge color="warning" className="ms-2">
                Results limited to {results.limitApplied} rows
              </Badge>
            )}
          </div>
          <Button color="success" size="sm" outline onClick={handleDownloadCsv}>
            <Download size={14} className="me-1" /> Download CSV
          </Button>
        </div>
        <div
          className="table-responsive"
          style={{ maxHeight: "500px", overflow: "auto" }}
        >
          <Table bordered striped hover size="sm">
            <thead style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 1 }}>
              <tr>
                <th>#</th>
                {results.columns.map((col) => (
                  <th key={col}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {results.rows.map((row, index) => (
                <tr key={index}>
                  <td>{index + 1}</td>
                  {results.columns.map((col) => (
                    <td key={col}>
                      {row[col] === null ? (
                        <em className="text-muted">NULL</em>
                      ) : typeof row[col] === "object" ? (
                        JSON.stringify(row[col])
                      ) : (
                        String(row[col])
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </div>
    );
  };

  return (
    <Fragment>
      <Breadcrumb title="SQL Runner" parent="Admin" />
      <Container fluid={true}>
        <Row>
          <Col>
            <Alert color="warning" className="d-flex align-items-center">
              <AlertTriangle size={18} className="me-2" />
              <span>
                <strong>Admin Tool:</strong> Only SELECT and UPDATE queries are
                allowed. All queries are logged for audit purposes. UPDATE
                requires a WHERE clause. Results limited to 500 rows.
              </span>
            </Alert>
          </Col>
        </Row>

        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <Database size={18} className="me-2" />
                  Query Editor
                </h5>
                <div className="d-flex align-items-center gap-2">
                  <small className="text-muted me-3">
                    Ctrl+Enter to execute
                  </small>
                  <Button
                    color="primary"
                    onClick={handleExecute}
                    disabled={isLoading || !query?.trim()}
                  >
                    {isLoading ? (
                      <>
                        <Spinner size="sm" className="me-1" /> Executing...
                      </>
                    ) : (
                      <>
                        <Play size={14} className="me-1" /> Execute Query
                      </>
                    )}
                  </Button>
                </div>
              </CardHeader>
              <CardBody className="p-0" onKeyDown={handleKeyDown}>
                <Editor
                  height="250px"
                  defaultLanguage="sql"
                  value={query}
                  onChange={(value) => setQuery(value || "")}
                  onMount={handleEditorDidMount}
                  theme="vs-dark"
                  options={{
                    minimap: { enabled: false },
                    fontSize: 14,
                    lineNumbers: "on",
                    scrollBeyondLastLine: false,
                    wordWrap: "on",
                    automaticLayout: true,
                    suggestOnTriggerCharacters: true,
                    tabSize: 2,
                  }}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>

        {error && (
          <Row>
            <Col>
              <Alert color="danger">
                <strong>Error:</strong> {error}
              </Alert>
            </Col>
          </Row>
        )}

        {results && (
          <Row>
            <Col>
              <Card>
                <CardHeader>
                  <h5 className="mb-0">Results</h5>
                </CardHeader>
                <CardBody>{renderResultsTable()}</CardBody>
              </Card>
            </Col>
          </Row>
        )}

        <Row>
          <Col>
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">
                  <List size={18} className="me-2" />
                  Query History
                </h5>
                {historyPagination.total > 0 && (
                  <small className="text-muted">{historyPagination.total} total queries</small>
                )}
              </CardHeader>
              <CardBody>
                {historyLoading ? (
                  <div className="text-center py-4">
                    <Spinner size="sm" /> Loading history...
                  </div>
                ) : history.length === 0 ? (
                  <p className="text-muted text-center mb-0">No query history yet.</p>
                ) : (
                  <>
                    <div className="table-responsive" style={{ maxHeight: "400px", overflow: "auto" }}>
                      <Table bordered striped hover size="sm">
                        <thead style={{ position: "sticky", top: 0, background: "#f8f9fa", zIndex: 1 }}>
                          <tr>
                            <th>#</th>
                            <th>Date</th>
                            <th>User</th>
                            <th>Query</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Rows</th>
                            <th>Time</th>
                          </tr>
                        </thead>
                        <tbody>
                          {history.map((log, index) => (
                            <tr key={log.id}>
                              <td>{(historyPagination.page - 1) * 20 + index + 1}</td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                {new Date(log.createdAt).toLocaleString()}
                              </td>
                              <td>{log.userEmail || `User #${log.userId}`}</td>
                              <td
                                style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", cursor: "pointer" }}
                                title={log.queryText}
                                onClick={() => { setQuery(log.queryText); }}
                              >
                                <code style={{ fontSize: "12px" }}>{log.queryText}</code>
                              </td>
                              <td>
                                <Badge color={log.queryType === "SELECT" ? "info" : "warning"}>
                                  {log.queryType}
                                </Badge>
                              </td>
                              <td>
                                <Badge color={log.status === "success" ? "success" : log.status === "blocked" ? "warning" : "danger"}>
                                  {log.status}
                                </Badge>
                              </td>
                              <td>{log.rowsAffected ?? "-"}</td>
                              <td>{log.executionTimeMs ? `${log.executionTimeMs}ms` : "-"}</td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    </div>
                    {historyPagination.totalPages > 1 && (
                      <Pagination className="mt-3 justify-content-center">
                        <PaginationItem disabled={historyPagination.page <= 1}>
                          <PaginationLink previous onClick={() => fetchHistory(historyPagination.page - 1)} />
                        </PaginationItem>
                        {Array.from({ length: Math.min(historyPagination.totalPages, 5) }, (_, i) => {
                          let pageNum;
                          if (historyPagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (historyPagination.page <= 3) {
                            pageNum = i + 1;
                          } else if (historyPagination.page >= historyPagination.totalPages - 2) {
                            pageNum = historyPagination.totalPages - 4 + i;
                          } else {
                            pageNum = historyPagination.page - 2 + i;
                          }
                          return (
                            <PaginationItem key={pageNum} active={pageNum === historyPagination.page}>
                              <PaginationLink onClick={() => fetchHistory(pageNum)}>{pageNum}</PaginationLink>
                            </PaginationItem>
                          );
                        })}
                        <PaginationItem disabled={historyPagination.page >= historyPagination.totalPages}>
                          <PaginationLink next onClick={() => fetchHistory(historyPagination.page + 1)} />
                        </PaginationItem>
                      </Pagination>
                    )}
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default SqlRunner;
